import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trophy, Crown, Medal, ArrowLeft, Flame, Star, Users } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const API = process.env.REACT_APP_BACKEND_URL;

const TIER_COLORS = {
  seeker: { bg: 'bg-amber-900/30', text: 'text-amber-400', border: 'border-amber-700/30' },
  berean: { bg: 'bg-blue-900/30', text: 'text-blue-400', border: 'border-blue-700/30' },
  scholar: { bg: 'bg-purple-900/30', text: 'text-purple-400', border: 'border-purple-700/30' },
  church: { bg: 'bg-emerald-900/30', text: 'text-emerald-400', border: 'border-emerald-700/30' },
};

const RANK_ICONS = [Crown, Trophy, Medal];
const RANK_COLORS = ['text-amber-400', 'text-slate-300', 'text-amber-700'];

const Leaderboard = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/api/leaderboard/`)
      .then(r => r.json())
      .then(d => { setData(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0806] text-amber-100" data-testid="leaderboard-page">
      <Header />

      <div className="container mx-auto px-4 py-10 max-w-3xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-sm text-amber-600/50 hover:text-amber-400 mb-8 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" /> Back
        </button>

        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-3 mb-3">
            <Trophy className="h-8 w-8 text-amber-500" />
            <h1 className="text-3xl sm:text-4xl font-serif text-amber-100">Treasure Seekers</h1>
          </div>
          <p className="text-amber-600/50 text-sm max-w-lg mx-auto">
            Top explorers who have studied the most puzzle pieces and unlocked the deepest knowledge
          </p>
          {data && (
            <div className="flex items-center justify-center gap-4 mt-4">
              <span className="text-xs font-mono text-amber-700/40 flex items-center gap-1">
                <Users className="h-3 w-3" /> {data.total_explorers} active explorers
              </span>
            </div>
          )}
        </div>

        {loading ? (
          <div className="text-center py-20 text-amber-600/50 animate-pulse">Loading leaderboard...</div>
        ) : !data || data.leaderboard.length === 0 ? (
          <div className="text-center py-20">
            <Flame className="h-12 w-12 text-amber-800/30 mx-auto mb-4" />
            <p className="text-amber-700/50 font-serif">No explorers yet. Be the first to study the puzzle pieces!</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="leaderboard-list">
            {data.leaderboard.map((entry, i) => {
              const isTop3 = i < 3;
              const RankIcon = RANK_ICONS[i] || Star;
              const rankColor = RANK_COLORS[i] || 'text-amber-800/40';
              const tierStyle = TIER_COLORS[entry.tier] || TIER_COLORS.seeker;

              return (
                <div
                  key={entry.user_id}
                  data-testid={`leaderboard-rank-${entry.rank}`}
                  className={`flex items-center gap-4 px-5 py-4 rounded-xl border transition-all ${
                    isTop3
                      ? 'bg-gradient-to-r from-amber-900/20 to-transparent border-amber-700/30'
                      : 'bg-[#0f0c08] border-amber-900/15 hover:border-amber-800/30'
                  }`}
                >
                  {/* Rank */}
                  <div className="w-10 text-center shrink-0">
                    {isTop3 ? (
                      <RankIcon className={`h-6 w-6 mx-auto ${rankColor}`} />
                    ) : (
                      <span className="text-lg font-mono font-bold text-amber-800/40">#{entry.rank}</span>
                    )}
                  </div>

                  {/* Name & Tier */}
                  <div className="flex-1 min-w-0">
                    <p className={`font-serif font-medium truncate ${isTop3 ? 'text-amber-100' : 'text-amber-200/70'}`}>
                      {entry.name}
                    </p>
                    <span className={`inline-block text-[9px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-full mt-1 border ${tierStyle.bg} ${tierStyle.text} ${tierStyle.border}`}>
                      {entry.tier === 'church' ? 'Pastor/Church' : entry.tier}
                    </span>
                  </div>

                  {/* Pieces count */}
                  <div className="text-right shrink-0">
                    <p className={`text-xl font-mono font-bold ${isTop3 ? 'text-amber-300' : 'text-amber-500/60'}`}>
                      {entry.pieces_studied}
                    </p>
                    <p className="text-[9px] text-amber-700/40 font-mono uppercase">pieces</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default Leaderboard;
